/**
 * VolumePricingSection — Switch-gated volume pricing for PricingCard.
 *
 * Switch OFF: "Volume Pricing [toggle]" — minimal one-liner
 * Switch ON (no tiers yet): Opens editor to set up tiers
 * Switch ON (has tiers): "Volume Pricing  3 tiers  [Edit]  [toggle]"
 * Toggle OFF: Removes all tiers with confirmation
 *
 * Part of Plan 45 (Volume Pricing), Epic 02.
 */

import { useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useVolumeTiers, useSaveVolumeTiers } from "@/features/inventory/api/volumeTierHooks";
import { VolumePricingEditor } from "./VolumePricingEditor";

// ── Props ────────────────────────────────────────────────────

interface VolumePricingSectionProps {
  itemCode: string;
  basePrice: number | null;
  currency: string;
}

// ── Component ────────────────────────────────────────────────

export function VolumePricingSection({
  itemCode,
  basePrice,
  currency,
}: VolumePricingSectionProps) {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isConfirmingOff, setIsConfirmingOff] = useState(false);
  const tiersQuery = useVolumeTiers(itemCode);
  const saveMutation = useSaveVolumeTiers(itemCode);

  const tiers = tiersQuery.data ?? [];
  const hasTiers = tiers.length > 0;
  const hasBasePrice = basePrice != null && basePrice > 0;

  const handleToggle = useCallback((checked: boolean) => {
    if (!hasBasePrice) return;

    if (checked) {
      setIsEditorOpen(true);
    } else if (hasTiers) {
      setIsConfirmingOff(true);
    }
  }, [hasBasePrice, hasTiers]);

  const handleConfirmRemove = useCallback(() => {
    saveMutation.mutate(
      { item_code: itemCode, tiers: [] },
      {
        onSuccess: () => {
          setIsConfirmingOff(false);
          toast.success("Volume pricing removed");
        },
        onError: () => {
          setIsConfirmingOff(false);
          toast.error("Failed to remove volume pricing");
        },
      }
    );
  }, [itemCode, saveMutation]);

  // Skip rendering during initial load (AFTER all hooks)
  if (tiersQuery.isLoading && !tiersQuery.data) return null;

  return (
    <>
      <div className="py-3 border-t border-border/20">
        {/* Main row: label + tier count + edit + switch */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Volume Pricing
            </span>
            {hasTiers && (
              <>
                <span className={cn(
                  "text-xs text-muted-foreground tabular-nums transition-opacity duration-150",
                  tiersQuery.isFetching ? "opacity-60" : "opacity-100"
                )}>
                  {tiers.length} {tiers.length === 1 ? "tier" : "tiers"}
                </span>
                <button
                  type="button"
                  onClick={() => setIsEditorOpen(true)}
                  className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  Edit
                </button>
              </>
            )}
          </div>

          <Switch
            checked={hasTiers}
            onCheckedChange={handleToggle}
            disabled={!hasBasePrice || saveMutation.isPending}
            aria-label="Enable volume pricing"
          />
        </div>

        {/* Inline confirmation when toggling off */}
        {isConfirmingOff && (
          <div className="mt-2 flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Remove all tiers?</span>
            <button
              type="button"
              onClick={handleConfirmRemove}
              disabled={saveMutation.isPending}
              className="font-medium text-red-600 hover:text-red-700 transition-colors"
            >
              {saveMutation.isPending ? "Removing..." : "Remove"}
            </button>
            <button
              type="button"
              onClick={() => setIsConfirmingOff(false)}
              className="font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Editor modal */}
      {hasBasePrice && (
        <VolumePricingEditor
          open={isEditorOpen}
          onOpenChange={setIsEditorOpen}
          itemCode={itemCode}
          basePrice={basePrice}
          currency={currency}
          existingTiers={tiers}
        />
      )}
    </>
  );
}
